// WAAP Programmable Mitigation (PBR) — Web Access Controls + Rate Limitings.
//
// PUBLIC Open-API surface (root conversant SDK, HMAC) — two account-level
// sub-resources hang off the vendor `/waap/v1.0/pbrStrategy/*` surface. Rules
// span every website and apply to a selected set ("Apply to"). The BE endpoint
// is domain-scoped (any domainId resolves the account's customer server-side),
// so the FE passes a representative domainId from useWaapWebsite().selected.
//
//   GET    /waap/domains/:domainId/pbr/web-access-controls
//   POST   /waap/domains/:domainId/pbr/web-access-controls
//   PUT    /waap/domains/:domainId/pbr/web-access-controls/:ruleId
//   DELETE /waap/domains/:domainId/pbr/web-access-controls/:ruleId
//   GET    /waap/domains/:domainId/pbr/rate-limitings
//   POST   /waap/domains/:domainId/pbr/rate-limitings
//   PUT    /waap/domains/:domainId/pbr/rate-limitings/:ruleId
//   DELETE /waap/domains/:domainId/pbr/rate-limitings/:ruleId
//
// Migrated from the PRIVATE SSO surface (/pbr/*). Field names follow
// the public DTO exactly (pkg/handlers/cdns/conversant/waap_pbr_public_ops.go):
//   path_match_condition → path_operator   path        → path_pattern
//   key                  → key_type        percent     → triggering_probability
//   auto_list_type       → auto_list_key   auto_list   → auto_list_duration
//   sites                → websites        (+ key_regex on Web Access Control)
// The private `action_list` and `filter_conditions` have NO public equivalent
// and were dropped (the public PBR DTO carries neither). The Rate Limiting
// private `time_window_value` (window-length multiplier) is also gone — the
// public surface expresses the window as `window_unit` + `max_requests` only.
//
// NOTE — no public GET-by-id route exists (only list/create/update/delete), so
// the edit-form detail hooks read the full list and find the row by id (the
// list endpoint returns the complete DTO per row, not a trimmed projection).
//
// CREATE may echo a partial object from the vendor; we don't trust it — every
// write invalidates the list query, so the form refetches the authoritative GET.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

// ---- Enabled sites (KEPT PRIVATE) ----------------------------------------
// The public Open-API has NO `/pbr/enabled-sites` equivalent, so the
// Apply-to picker + the list's Website column still read the private SSO route.
// CAVEAT: the public rule's `websites` are vendor website IDs; this private
// `PbrEnabledSite.id` space MUST be verified to match. If they diverge, the
// Website column falls back to `#<id>` (the picker checkboxes would mis-map and
// the picker should then move to GET /websites). Verify before relying.

/** A WAAP-protected site eligible for a rule (private portal /sites/enabled). */
export interface PbrEnabledSite {
  id: number;
  domain: string;
  domain_id: number;
  protection: boolean;
  protectionMode: string;
  status: number;
  customer_id: number;
}

// ---- Web Access Control DTOs ---------------------------------------------

/** The clean create/update body the FE sends (public DTO, sans id). */
export interface WebAccessControlRuleInput {
  name: string;
  description: string;
  path_operator: string;
  path_pattern: string;
  key_type: string;
  key_value: string[];
  /** Treat key_value entries as regular expressions. */
  key_regex: boolean;
  dev_type: string[];
  triggering_probability: number;
  auto_list_key: string;
  auto_list_duration: number;
  /** Website IDs this rule applies to (vendor website id space). */
  websites: number[];
}

/** GET-detail (and list-row) body — the input shape plus the strategy id. */
export interface WebAccessControlRuleDetail extends WebAccessControlRuleInput {
  id: number;
}

/** A row in the Web Access Control list table. */
export interface WebAccessControlRuleListItem {
  id: number;
  name: string;
  description: string;
  /** Website IDs the rule applies to (resolved to names via enabled-sites). */
  websites: number[];
}

/** A blank Web Access Control rule with the public defaults. */
export function emptyWebAccessControlRule(): WebAccessControlRuleInput {
  return {
    name: '',
    description: '',
    path_operator: 'equal',
    path_pattern: '',
    key_type: 'ip',
    key_value: [],
    key_regex: false,
    dev_type: ['pc', 'mac', 'mobile'],
    triggering_probability: 100,
    auto_list_key: 'ip',
    // Vendor rejects a 0/empty duration on create (AutoListDuration.Invalid), so
    // default to 1h; the form lets the user change it.
    auto_list_duration: 3600,
    websites: [],
  };
}

// ---- Rate Limiting DTOs --------------------------------------------------

/** The clean create/update body for a Rate Limiting rule (public DTO, sans id).
 *  Shares the path/key/device/auto-list shape with Web Access Control but adds
 *  the rate-measurement fields and has no per-key value list. */
export interface RateLimitRuleInput {
  name: string;
  description: string;
  path_operator: string;
  path_pattern: string;
  key_type: string;
  /** Rate measurement mode (PBR_RATE_MEASUREMENTS): 'rate' = QPS, 'timeWindow' = window. */
  rate_limit_type: string;
  /** QPS mode: queries per second. 0 in window mode. */
  rate: number;
  /** QPS mode only: burst allowance. */
  burst: number;
  /** Window unit (PBR_TIME_WINDOW_UNITS); carried even in QPS mode. */
  window_unit: string;
  /** Window mode: max requests per window unit. 0 in QPS mode. */
  max_requests: number;
  dev_type: string[];
  triggering_probability: number;
  auto_list_key: string;
  auto_list_duration: number;
  /** Website IDs this rule applies to (vendor website id space). */
  websites: number[];
}

/** GET-detail (and list-row) body for Rate Limiting (input + strategy id). */
export interface RateLimitRuleDetail extends RateLimitRuleInput {
  id: number;
}

/** A blank Rate Limiting rule with the public QPS defaults. */
export function emptyRateLimitRule(): RateLimitRuleInput {
  return {
    name: '',
    description: '',
    path_operator: 'equal',
    path_pattern: '',
    key_type: 'ip',
    rate_limit_type: 'rate',
    rate: 1,
    burst: 0,
    window_unit: 'minute',
    max_requests: 0,
    dev_type: ['pc', 'mac', 'mobile'],
    triggering_probability: 100,
    auto_list_key: 'ip',
    // Vendor rejects a 0/empty duration on create (AutoListDuration.Invalid), so
    // default to 1h; the form lets the user change it.
    auto_list_duration: 3600,
    websites: [],
  };
}

// ---- Catalogs ------------------------------------------------------------
// `equal` / `ip` / `rate` / `minute` are confirmed from captured payloads; the
// rest follow the portal dropdowns and SHOULD be verified before relying on them
// (the vendor 400s on unknown enum values).

// pathOperator — exact vendor enum (public Open-API PBR Web Access Control +
// Rate Limiting docs). Shared by both rule types.
export const PBR_PATH_CONDITIONS: { value: string; label: string }[] = [
  { value: 'equal', label: 'Equal to' },
  { value: 'notEqual', label: 'Not equal to' },
  { value: 'contain', label: 'Contains' },
  { value: 'notContain', label: 'Does not contain' },
  { value: 'beginsWith', label: 'Begins with' },
  { value: 'notBeginsWith', label: 'Does not begin with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'notEndsWith', label: 'Does not end with' },
  { value: 'regex', label: 'Regex' },
  { value: 'notMatchRegex', label: 'Does not match regex' },
  { value: 'include', label: 'Include' },
  { value: 'exclude', label: 'Exclude' },
];

// keyType — differs by rule type: Web Access Control allows userAgent; Rate
// Limiting does NOT (doc: ip/fingerprint/cookieId only).
export const PBR_WEB_ACCESS_KEYS: { value: string; label: string }[] = [
  { value: 'ip', label: 'IP' },
  { value: 'fingerprint', label: 'Fingerprint' },
  { value: 'userAgent', label: 'User-Agent' },
  { value: 'cookieId', label: 'Cookie ID' },
];

export const PBR_RATE_LIMIT_KEYS: { value: string; label: string }[] = [
  { value: 'ip', label: 'IP' },
  { value: 'fingerprint', label: 'Fingerprint' },
  { value: 'cookieId', label: 'Cookie ID' },
];

export const PBR_DEVICE_TYPES: { value: string; label: string }[] = [
  { value: 'pc', label: 'PC' },
  { value: 'mac', label: 'MAC' },
  { value: 'mobile', label: 'Mobile Devices' },
];

/** Auto-Web ACL key type. */
export const PBR_AUTO_LIST_KEYS: { value: string; label: string }[] = [
  { value: 'ip', label: 'IP' },
  { value: 'fingerprint', label: 'Fingerprint' },
];

/** Triggering-probability presets (100% … 10%). */
export const PBR_PROBABILITIES: number[] = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10];

/** Rate measurement mode — the vendor `rate_limit_type`. QPS limits a per-second
 *  rate (with a burst); Time Window limits a count per window unit. */
export const PBR_RATE_MEASUREMENTS: { value: string; label: string }[] = [
  { value: 'rate', label: 'Queries per second (QPS)' },
  { value: 'timeWindow', label: 'Queries per Time Window' },
];

/** Time-window unit (`window_unit`). */
export const PBR_TIME_WINDOW_UNITS: { value: string; label: string }[] = [
  { value: 'second', label: 'Second' }, // TODO verify
  { value: 'minute', label: 'Minute' }, // confirmed
  { value: 'hour', label: 'Hour' }, // TODO verify
];

// ---- Paths + query keys --------------------------------------------------

const pbrBase = (domainId: string) => `/waap/domains/${domainId}/pbr`;
const webAccessPath = (domainId: string) => `${pbrBase(domainId)}/web-access-controls`;
// Rate Limiting runs on the PRIVATE SSO surface: the public Open-API PBR is
// vendor-broken (its PUT silently no-ops and its GET mis-reports rate_limit_type).
// The private /pbr/rate-limits/ routes round-trip the real freq_limit_type config.
const privateRatePath = (domainId: string) => `/waap/domains/${domainId}/pbr/rate-limits`;
// Enabled sites stays on the private SSO surface (no public equivalent).
const enabledSitesPath = (domainId: string) =>
  `/waap/domains/${domainId}/pbr/enabled-sites`;

export const pbrKeys = {
  all: ['waap', 'pbr'] as const,
  rules: (domainId: string) => ['waap', 'pbr', domainId, 'web-access-controls'] as const,
  rateRules: (domainId: string) => ['waap', 'pbr', domainId, 'rate-limitings'] as const,
  enabledSites: (domainId: string) => ['waap', 'pbr', domainId, 'enabled-sites'] as const,
};

// ---- helpers -------------------------------------------------------------

function asArray<T = unknown>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    for (const k of ['data', 'list', 'items', 'results']) {
      const v = (raw as Record<string, unknown>)[k];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}

// Both WAC and Rate-Limit detail rows share the list-table fields, so this maps
// the common subset for either.
const toWebAccessListItem = (
  r: Pick<WebAccessControlRuleDetail, 'id' | 'name' | 'description' | 'websites'>,
): WebAccessControlRuleListItem => ({
  id: Number(r.id),
  name: String(r.name ?? ''),
  description: String(r.description ?? ''),
  websites: Array.isArray(r.websites) ? r.websites.map(Number).filter(Number.isFinite) : [],
});

// ---- Enabled sites hook (private) ----------------------------------------

/** Enabled sites for the Apply-to picker + the list's Website column. Reads the
 *  PRIVATE SSO route — see the file header; there is no public equivalent. */
export function useWaapEnabledSites(domainId: string | undefined) {
  return useQuery({
    queryKey: pbrKeys.enabledSites(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<PbrEnabledSite[]> => {
      const res = await serviceApiV3.get<unknown>(enabledSitesPath(domainId as string));
      return asArray<PbrEnabledSite>(res.data);
    },
  });
}

// ---- Web Access Control hooks --------------------------------------------

/** List Web Access Control rules (account-wide; public returns [] when none). */
export function useWebAccessControlRules(domainId: string | undefined) {
  return useQuery({
    queryKey: pbrKeys.rules(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WebAccessControlRuleListItem[]> => {
      const res = await serviceApiV3.get<unknown>(webAccessPath(domainId as string));
      return asArray<WebAccessControlRuleDetail>(res.data)
        .map(toWebAccessListItem)
        .filter((r) => Number.isFinite(r.id));
    },
  });
}

/** Fetch one rule's full detail (for the edit form). No public GET-by-id route
 *  exists, so we read the list and find the row by id. */
export function useWebAccessControlRule(domainId: string | undefined, ruleId: string | undefined) {
  return useQuery({
    queryKey: [...pbrKeys.rules(domainId ?? ''), ruleId ?? ''] as const,
    enabled: !!domainId && !!ruleId,
    retry: false,
    queryFn: async (): Promise<WebAccessControlRuleDetail | null> => {
      const res = await serviceApiV3.get<unknown>(webAccessPath(domainId as string));
      const rows = asArray<WebAccessControlRuleDetail>(res.data);
      return rows.find((r) => String(r.id) === String(ruleId)) ?? null;
    },
  });
}

/** Create a Web Access Control rule. Invalidates the list (CREATE echo untrusted). */
export function useCreateWebAccessControlRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WebAccessControlRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WebAccessControlRuleInput) => {
      const res = await serviceApiV3.post(webAccessPath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a Web Access Control rule (full replace via PUT). */
export function useUpdateWebAccessControlRule(
  domainId: string,
  ruleId: string,
  options?: UseMutationOptions<unknown, unknown, WebAccessControlRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WebAccessControlRuleInput) => {
      const res = await serviceApiV3.put(`${webAccessPath(domainId)}/${ruleId}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a Web Access Control rule by its strategy id. */
export function useDeleteWebAccessControlRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${webAccessPath(domainId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Rate Limiting hooks (PRIVATE surface) -------------------------------
// The public Open-API PBR rate-limiting is vendor-broken both ways (PUT no-ops,
// GET mis-reads the mode), so this whole feature runs on the private /pbr/
// rate-limits/ routes, which round-trip the vendor's native config. The FE keeps
// its clean public-shaped DTO; these adapters translate to/from the private wire
// shape (freq_limit_type/high_freq/time_window_value/path_match_condition/…).

/** Vendor-native rate-limit fields the private detail endpoint returns/accepts. */
interface PrivateRateDetail {
  id?: number;
  name?: string;
  description?: string;
  path?: string;
  path_match_condition?: string;
  key?: string;
  freq_limit_type?: string; // "time_window" = Time Window; "rate" = QPS
  high_freq?: number;
  burst?: number;
  time_window_type?: string;
  time_window_value?: number;
  dev_type?: string[];
  percent?: number;
  auto_list_type?: string;
  auto_list?: number;
  sites?: number[];
}

/** private detail → clean public DTO the edit form consumes. */
function rateFromPrivate(p: PrivateRateDetail): RateLimitRuleDetail {
  return {
    id: Number(p.id ?? 0),
    name: p.name ?? '',
    description: p.description ?? '',
    path_operator: p.path_match_condition ?? 'equal',
    path_pattern: p.path ?? '',
    key_type: p.key ?? 'ip',
    rate_limit_type: p.freq_limit_type === 'time_window' ? 'timeWindow' : 'rate',
    rate: p.high_freq ?? 0,
    burst: p.burst ?? 0,
    window_unit: p.time_window_type ?? 'minute',
    max_requests: p.time_window_value ?? 0,
    dev_type: p.dev_type ?? [],
    triggering_probability: p.percent ?? 100,
    auto_list_key: p.auto_list_type ?? 'ip',
    auto_list_duration: p.auto_list ?? 0,
    websites: Array.isArray(p.sites) ? p.sites.map(Number).filter(Number.isFinite) : [],
  };
}

/** clean public DTO → private create/update body. The action_list (block 400)
 *  and empty filter_conditions mirror the vendor portal's own save. */
function rateToPrivate(b: RateLimitRuleInput) {
  return {
    name: b.name,
    description: b.description,
    path: b.path_pattern,
    path_match_condition: b.path_operator,
    key: b.key_type,
    freq_limit_type: b.rate_limit_type === 'timeWindow' ? 'time_window' : 'rate',
    high_freq: b.rate,
    burst: b.burst,
    time_window_type: b.window_unit,
    time_window_value: b.max_requests,
    dev_type: b.dev_type,
    percent: b.triggering_probability,
    auto_list_type: b.auto_list_key,
    auto_list: b.auto_list_duration,
    action_list: [{ action: 'block_code', action_value: 400, delay: 0, weight: 1 }],
    filter_conditions: [],
    sites: b.websites,
  };
}

/** private list row (raw vendor summary) → list item. */
function rateListItemFromPrivate(r: {
  id?: number;
  name?: string;
  description?: string;
  sites?: { site_id?: number }[];
}): WebAccessControlRuleListItem {
  return {
    id: Number(r.id ?? NaN),
    name: String(r.name ?? ''),
    description: String(r.description ?? ''),
    websites: Array.isArray(r.sites)
      ? r.sites.map((s) => Number(s?.site_id)).filter(Number.isFinite)
      : [],
  };
}

/** List Rate Limiting rules (account-wide, private). */
export function useRateLimitRules(domainId: string | undefined) {
  return useQuery({
    queryKey: pbrKeys.rateRules(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WebAccessControlRuleListItem[]> => {
      const res = await serviceApiV3.get<unknown>(privateRatePath(domainId as string));
      return asArray<Parameters<typeof rateListItemFromPrivate>[0]>(res.data)
        .map(rateListItemFromPrivate)
        .filter((r) => Number.isFinite(r.id));
    },
  });
}

/** Fetch one rate-limit rule's full detail (private GET-by-id → real config). */
export function useRateLimitRule(domainId: string | undefined, ruleId: string | undefined) {
  return useQuery({
    queryKey: [...pbrKeys.rateRules(domainId ?? ''), ruleId ?? ''] as const,
    enabled: !!domainId && !!ruleId,
    retry: false,
    queryFn: async (): Promise<RateLimitRuleDetail | null> => {
      const res = await serviceApiV3.get<PrivateRateDetail>(
        `${privateRatePath(domainId as string)}/${ruleId}`,
      );
      return res.data ? rateFromPrivate(res.data) : null;
    },
  });
}

/** Create a Rate Limiting rule (private two-call create handled server-side). */
export function useCreateRateLimitRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, RateLimitRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RateLimitRuleInput) => {
      const res = await serviceApiV3.post(privateRatePath(domainId), rateToPrivate(body));
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrKeys.rateRules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a Rate Limiting rule (private whole-object PUT). */
export function useUpdateRateLimitRule(
  domainId: string,
  ruleId: string,
  options?: UseMutationOptions<unknown, unknown, RateLimitRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RateLimitRuleInput) => {
      const res = await serviceApiV3.put(`${privateRatePath(domainId)}/${ruleId}`, rateToPrivate(body));
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrKeys.rateRules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a Rate Limiting rule by its strategy id (private). */
export function useDeleteRateLimitRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${privateRatePath(domainId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrKeys.rateRules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------
// Mirror the domain-anchored hooks above but build /services/${serviceId}/waap/pbr/...
// so Programmable Mitigation renders without a carrier domain anchor.

const servicePbrBase = (serviceId: string) => `/services/${serviceId}/waap/pbr`;
const serviceWebAccessPath = (serviceId: string) =>
  `${servicePbrBase(serviceId)}/web-access-controls`;
const serviceRatePath = (serviceId: string) => `${servicePbrBase(serviceId)}/rate-limits`;
const servicePbrEnabledSitesPath = (serviceId: string) =>
  `${servicePbrBase(serviceId)}/enabled-sites`;

export const pbrServiceKeys = {
  rules: (serviceId: string) =>
    ['waap', 'pbr', 'service', serviceId, 'web-access-controls'] as const,
  rateRules: (serviceId: string) =>
    ['waap', 'pbr', 'service', serviceId, 'rate-limits'] as const,
  enabledSites: (serviceId: string) =>
    ['waap', 'pbr', 'service', serviceId, 'enabled-sites'] as const,
};

/** Enabled sites for the Apply-to picker, service-scoped. */
export function useServiceWaapEnabledSites(serviceId: string | undefined) {
  return useQuery({
    queryKey: pbrServiceKeys.enabledSites(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<PbrEnabledSite[]> => {
      const res = await serviceApiV3.get<unknown>(
        servicePbrEnabledSitesPath(serviceId as string),
      );
      return asArray<PbrEnabledSite>(res.data);
    },
  });
}

/** List Web Access Control rules, service-scoped. */
export function useServiceWebAccessControlRules(serviceId: string | undefined) {
  return useQuery({
    queryKey: pbrServiceKeys.rules(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<WebAccessControlRuleListItem[]> => {
      const res = await serviceApiV3.get<unknown>(serviceWebAccessPath(serviceId as string));
      return asArray<WebAccessControlRuleDetail>(res.data)
        .map(toWebAccessListItem)
        .filter((r) => Number.isFinite(r.id));
    },
  });
}

/** Fetch one Web Access Control rule's full detail, service-scoped (GET-by-id available). */
export function useServiceWebAccessControlRule(
  serviceId: string | undefined,
  ruleId: string | undefined,
) {
  return useQuery({
    queryKey: [...pbrServiceKeys.rules(serviceId ?? ''), ruleId ?? ''] as const,
    enabled: !!serviceId && !!ruleId,
    retry: false,
    queryFn: async (): Promise<WebAccessControlRuleDetail | null> => {
      const res = await serviceApiV3.get<unknown>(
        `${serviceWebAccessPath(serviceId as string)}/${ruleId}`,
      );
      if (!res.data) return null;
      const body = res.data as WebAccessControlRuleDetail | { data?: WebAccessControlRuleDetail };
      return (body as { data?: WebAccessControlRuleDetail })?.data ??
        (body as WebAccessControlRuleDetail);
    },
  });
}

/** Create a Web Access Control rule, service-scoped. */
export function useCreateServiceWebAccessControlRule(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, WebAccessControlRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WebAccessControlRuleInput) => {
      const res = await serviceApiV3.post(serviceWebAccessPath(serviceId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrServiceKeys.rules(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a Web Access Control rule (full replace via PUT), service-scoped. */
export function useUpdateServiceWebAccessControlRule(
  serviceId: string,
  ruleId: string,
  options?: UseMutationOptions<unknown, unknown, WebAccessControlRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WebAccessControlRuleInput) => {
      const res = await serviceApiV3.put(`${serviceWebAccessPath(serviceId)}/${ruleId}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrServiceKeys.rules(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a Web Access Control rule, service-scoped. */
export function useDeleteServiceWebAccessControlRule(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${serviceWebAccessPath(serviceId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrServiceKeys.rules(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** List Rate Limiting rules, service-scoped. */
export function useServiceRateLimitRules(serviceId: string | undefined) {
  return useQuery({
    queryKey: pbrServiceKeys.rateRules(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<WebAccessControlRuleListItem[]> => {
      const res = await serviceApiV3.get<unknown>(serviceRatePath(serviceId as string));
      return asArray<Parameters<typeof rateListItemFromPrivate>[0]>(res.data)
        .map(rateListItemFromPrivate)
        .filter((r) => Number.isFinite(r.id));
    },
  });
}

/** Fetch one Rate Limiting rule's full detail, service-scoped (GET-by-id available). */
export function useServiceRateLimitRule(
  serviceId: string | undefined,
  ruleId: string | undefined,
) {
  return useQuery({
    queryKey: [...pbrServiceKeys.rateRules(serviceId ?? ''), ruleId ?? ''] as const,
    enabled: !!serviceId && !!ruleId,
    retry: false,
    queryFn: async (): Promise<RateLimitRuleDetail | null> => {
      const res = await serviceApiV3.get<PrivateRateDetail>(
        `${serviceRatePath(serviceId as string)}/${ruleId}`,
      );
      return res.data ? rateFromPrivate(res.data) : null;
    },
  });
}

/** Create a Rate Limiting rule, service-scoped. */
export function useCreateServiceRateLimitRule(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, RateLimitRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RateLimitRuleInput) => {
      const res = await serviceApiV3.post(serviceRatePath(serviceId), rateToPrivate(body));
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrServiceKeys.rateRules(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a Rate Limiting rule, service-scoped. */
export function useUpdateServiceRateLimitRule(
  serviceId: string,
  ruleId: string,
  options?: UseMutationOptions<unknown, unknown, RateLimitRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RateLimitRuleInput) => {
      const res = await serviceApiV3.put(
        `${serviceRatePath(serviceId)}/${ruleId}`,
        rateToPrivate(body),
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrServiceKeys.rateRules(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a Rate Limiting rule, service-scoped. */
export function useDeleteServiceRateLimitRule(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${serviceRatePath(serviceId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pbrServiceKeys.rateRules(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}
